import { generateSingleEmbedding } from './openaiService'

// Limiar de similaridade mínima para aceitar um match (0.0 a 1.0)
// Valores típicos: 0.7 (permissivo), 0.8 (balanceado), 0.9 (rigoroso)
export const SIMILARITY_THRESHOLD = 0.8

// Importação dinâmica dos embeddings
let embeddingsCache: Record<string, number[]> | null = null

async function loadEmbeddings(): Promise<Record<string, number[]>> {
    if (embeddingsCache) {
        return embeddingsCache
    }

    try {
        const response = await fetch('/data/embeddings.json')
        if (!response.ok) {
            throw new Error('Não foi possível carregar embeddings')
        }
        embeddingsCache = await response.json()
        return embeddingsCache!
    } catch (error) {
        console.error('Erro ao carregar embeddings:', error)
        // Fallback: tenta importar diretamente
        const embeddings = require('../data/embeddings.json')
        embeddingsCache = embeddings
        return embeddings
    }
}

export interface SimilarityResult {
    word: string
    similarity: number
}

/**
 * Calcula a similaridade de cosseno entre dois vetores
 */
function cosineSimilarity(vecA: number[], vecB: number[]): number {
    if (vecA.length !== vecB.length) {
        throw new Error('Vetores devem ter o mesmo tamanho')
    }

    let dotProduct = 0
    let magA = 0
    let magB = 0

    for (let i = 0; i < vecA.length; i++) {
        dotProduct += vecA[i] * vecB[i]
        magA += vecA[i] * vecA[i]
        magB += vecB[i] * vecB[i]
    }

    magA = Math.sqrt(magA)
    magB = Math.sqrt(magB)

    if (magA === 0 || magB === 0) {
        return 0
    }

    return dotProduct / (magA * magB)
}

/**
 * Encontra o sinal mais similar a uma palavra usando embeddings
 * Retorna apenas se a similaridade for >= SIMILARITY_THRESHOLD (0.8)
 */
export async function findSimilarSign(
    word: string,
): Promise<SimilarityResult | null> {
    try {
        // Gera embedding para a palavra
        const wordEmbedding = await generateSingleEmbedding(word)

        // Carrega embeddings das animações
        const embeddings = await loadEmbeddings()

        // Calcula similaridade com todas as animações
        let bestMatch: SimilarityResult = {
            word: '',
            similarity: -1,
        }

        for (const [animWord, animEmbedding] of Object.entries(embeddings)) {
            const similarity = cosineSimilarity(wordEmbedding, animEmbedding)
            if (similarity > bestMatch.similarity) {
                bestMatch = {
                    word: animWord,
                    similarity: similarity,
                }
            }
        }

        // Retorna null se a similaridade for muito baixa
        if (bestMatch.similarity < SIMILARITY_THRESHOLD) {
            console.warn(
                `Palavra "${word}" com baixa similaridade: ${bestMatch.similarity.toFixed(
                    3,
                )} (limiar: ${SIMILARITY_THRESHOLD})`,
            )
            return null
        }

        return bestMatch
    } catch (error) {
        console.error('Erro ao buscar sinal similar:', error)
        throw error
    }
}

/**
 * Encontra múltiplos sinais similares para uma palavra
 */
export async function findTopSimilarSigns(
    word: string,
    topN: number = 5,
): Promise<SimilarityResult[]> {
    try {
        // Gera embedding para a palavra
        const wordEmbedding = await generateSingleEmbedding(word)

        // Carrega embeddings das animações
        const embeddings = await loadEmbeddings()

        // Calcula similaridade com todas as animações
        const similarities: SimilarityResult[] = []

        for (const [animWord, animEmbedding] of Object.entries(embeddings)) {
            const similarity = cosineSimilarity(wordEmbedding, animEmbedding)
            similarities.push({
                word: animWord,
                similarity: similarity,
            })
        }

        // Ordena por similaridade decrescente e retorna os top N
        similarities.sort((a, b) => b.similarity - a.similarity)
        return similarities.slice(0, topN)
    } catch (error) {
        console.error('Erro ao buscar sinais similares:', error)
        throw error
    }
}

/**
 * Verifica se uma animação existe na base de dados
 */
export async function animationExists(word: string): Promise<boolean> {
    const embeddings = await loadEmbeddings()
    return word in embeddings
}

/**
 * Retorna a lista de todas as animações disponíveis
 */
export async function getAvailableAnimations(): Promise<string[]> {
    const embeddings = await loadEmbeddings()
    return Object.keys(embeddings)
}

/**
 * Converte uma palavra em datilologia (soletração letra por letra)
 * Retorna array de sinais das letras individuais
 */
function wordToDactylology(word: string): string[] {
    // Remove acentos e caracteres especiais, mantém apenas letras
    const normalized = word
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase()
        .replace(/[^A-Z]/g, '')

    // Converte cada letra para seu sinal correspondente
    const letters = normalized.split('')

    console.log(`Datilologia para "${word}": ${letters.join('-')}`)

    return letters
}

/**
 * Processa uma glosa e busca sinais similares para palavras não encontradas
 * Se não encontrar sinal similar, usa datilologia (soletração letra por letra)
 */
export async function processGlosaWithSimilarity(
    glosa: string[],
): Promise<
    {
        word: string
        animation: string
        similarity?: number
        isDactylology?: boolean
    }[]
> {
    const results: {
        word: string
        animation: string
        similarity?: number
        isDactylology?: boolean
    }[] = []

    for (const word of glosa) {
        if (await animationExists(word)) {
            // Animação existe, usa diretamente
            results.push({
                word,
                animation: word,
                similarity: 1.0,
                isDactylology: false,
            })
        } else {
            // Animação não existe, busca similar
            try {
                const similar = await findSimilarSign(word)

                if (similar === null) {
                    // Similaridade muito baixa, usa datilologia
                    const letters = wordToDactylology(word)

                    if (letters.length > 0) {
                        // Adiciona cada letra como uma animação separada
                        for (const letter of letters) {
                            results.push({
                                word: letter,
                                animation: letter,
                                similarity: 1.0,
                                isDactylology: true,
                            })
                        }
                        console.log(
                            `"${word}" será soletrado: ${letters.join(' ')}`,
                        )
                    } else {
                        // Se não houver letras válidas, pula
                        console.warn(
                            `Palavra "${word}" não pôde ser soletrada`,
                        )
                    }
                } else {
                    // Match válido
                    console.log(
                        `"${word}" -> "${
                            similar.word
                        }" (${similar.similarity.toFixed(3)})`,
                    )
                    results.push({
                        word,
                        animation: similar.word,
                        similarity: similar.similarity,
                        isDactylology: false,
                    })
                }
            } catch (error) {
                console.error(`Erro ao buscar similar para "${word}":`, error)
                // Em caso de erro, usa datilologia como fallback
                const letters = wordToDactylology(word)
                if (letters.length > 0) {
                    for (const letter of letters) {
                        results.push({
                            word: letter,
                            animation: letter,
                            similarity: 1.0,
                            isDactylology: true,
                        })
                    }
                    console.log(
                        `"${word}" será soletrado por erro: ${letters.join(
                            ' ',
                        )}`,
                    )
                }
            }
        }
    }

    return results
}
